"use client"

import React, { useEffect, useState } from 'react';
import { useUser, useAuth } from '@clerk/nextjs';
import { SubscriptionPlans } from '@/app/enum-subscription';
import useUserStore from '@/store/userStore';
import { useRouter } from 'next/navigation';
import {
    captureReplayWorthyError,
    flushReplay,
    markPaymentIntentForReplay
} from '@/utils/sentryReplay';

import { Check, Loader2, Film } from 'lucide-react';

const Pricing = () => {
    const { user, isLoaded } = useUser();
    const { userPlan } = useUserStore();
    const { userId } = useAuth();
    const router = useRouter();
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'yearly'

    // 过滤掉免费档位，并根据时长筛选
    const displayPlans = SubscriptionPlans.filter(p =>
        p.tier !== 'free' &&
        p.duration === (billingCycle === 'monthly' ? '/month' : '/year')
    );

    useEffect(() => {
        if (!isLoaded) return;

        markPaymentIntentForReplay('pricing_viewed', {
            authenticated: Boolean(userId)
        });
    }, [isLoaded, userId]);

    const handleSubscribe = async (plan) => {
        markPaymentIntentForReplay('subscribe_clicked', {
            planSlug: plan.slug,
            planTier: plan.tier,
            billingCycle
        });

        if (!userId) {
            // 未登录用户跳转到登录页面
            router.push('/create');
            return;
        }

        setLoadingPlan(plan.slug);
        try {
            const response = await fetch('/api/stripe/checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    priceId: plan.priceId,
                    userId: userId,
                    userEmail: user?.primaryEmailAddress?.emailAddress,
                    planSlug: plan.slug,
                }),
            });

            const data = await response.json();
            if (data.url) {
                markPaymentIntentForReplay('checkout_session_created', {
                    planSlug: plan.slug,
                    planTier: plan.tier
                });
                await flushReplay();
                window.location.href = data.url;
            } else {
                throw new Error(data.error || 'Failed to create checkout session');
            }
        } catch (error) {
            console.error('Subscription error:', error);
            captureReplayWorthyError(error, 'checkout_failed', {
                planSlug: plan.slug,
                planTier: plan.tier
            });
            alert('Payment initialization failed. Please try again.');
        } finally {
            setLoadingPlan(null);
        }
    };

    // 功能文案映射
    const renderFeatureItem = (key, value) => {
        if (value === false) return null;

        let label = "";
        switch (key) {
            case 'multiple_lip_sync_models': label = "Multiple lip sync models"; break;
            case 'select_video_loop_mode': label = "Select video loop mode"; break;
            case 'private_video_visibility': label = "Private video visibility"; break;
            case 'unlimited_text_to_audio': label = "Unlimited text to audio"; break;
            case 'permanent_storage': label = "Permanent Cloud Storage"; break;
            case 'video_upload_limit':
                label = value >= 999999 ? "Unlimited video upload" : `Video uploaded < ${value}MB`;
                break;
            case 'audio_upload_limit':
                label = value >= 999999 ? "Unlimited audio upload" : `Audio uploaded < ${value}MB`;
                break;
            default: return null;
        }

        return (
            <li key={key} className="flex items-center gap-3">
                <Check size={18} className="text-success shrink-0" />
                <span className="text-sm">{label}</span>
            </li>
        );
    };

    if (!isLoaded) return null;

    return (
        <div className="py-12 px-4 md:px-8 max-w-5xl mx-auto">
            {/* 周期切换 Tabs */}
            <div className="flex justify-center mb-12">
                <div className="tabs tabs-box bg-base-200 p-1 rounded-xl">
                    <button
                        className={`tab ${billingCycle === 'monthly' ? 'tab-active bg-base-100 shadow-sm' : ''} rounded-lg px-8`}
                        onClick={() => setBillingCycle('monthly')}
                    >
                        Monthly
                    </button>
                    <button
                        className={`tab ${billingCycle === 'yearly' ? 'tab-active bg-base-100 shadow-sm' : ''} rounded-lg px-8`}
                        onClick={() => setBillingCycle('yearly')}
                    >
                        Yearly <span className="ml-2 badge badge-success badge-sm">Save 20%</span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {displayPlans.map((plan) => (
                    <div key={plan.slug} className="bg-base-200 rounded-3xl p-8 flex flex-col border border-base-300 hover:border-primary/50 transition-all shadow-xl relative">
                        {plan.discountOff && (
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 badge badge-primary font-bold py-3">
                                Save {plan.discountOff}%
                            </div>
                        )}

                        <div className="mb-8">
                            <h2 className="text-xl font-bold mb-1">{plan.name}</h2>
                            <p className="text-sm text-base-content/60 mb-4">{plan.name} Plan of LipSyncShow</p>
                            <div className="flex flex-col">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-4xl font-black">${plan.price}</span>
                                    <span className="text-base-content/60">/month</span>
                                </div>
                                {billingCycle === 'yearly' && plan.price_yearly && (
                                    <div className="text-sm text-primary font-medium mt-1">
                                        Billed annually (${plan.price_yearly}/year)
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="divider opacity-50"></div>

                        <ul className="space-y-4 mb-12 flex-grow">
                            <li className="flex items-center gap-3">
                                <img src="/credits.svg" alt="credits" className="w-4 h-4" />
                                <span className="text-sm text-warning font-bold">{plan.credits} credits per month</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Film size={18} className="text-base-content/60 shrink-0" />
                                <span className="text-sm text-base-content/60">≈ {Math.floor(plan.credits)} seconds <span className="text-base-content/60 text-xs">per month</span></span>
                            </li>
                            {Object.entries(plan.features).map(([key, value]) => renderFeatureItem(key, value))}
                        </ul>

                        <button
                            onClick={() => handleSubscribe(plan)}
                            disabled={loadingPlan === plan.slug || (userId && (userPlan === plan.slug || userPlan?.startsWith(plan.tier)))}
                            className={`btn w-full rounded-xl h-14 text-lg font-bold shadow-lg ${(userId && (userPlan === plan.slug || userPlan?.startsWith(plan.tier)))
                                ? 'btn-disabled'
                                : 'bg-gradient-to-r from-purple-800 to-purple-700 hover:from-purple-600 hover:to-purple-400 text-white border-none transition-all duration-300'
                                }`}
                        >
                            {loadingPlan === plan.slug ? (
                                <Loader2 className="animate-spin" />
                            ) : (
                                (userId && (userPlan === plan.slug || userPlan?.startsWith(plan.tier))) ? 'Current Plan' : 'Subscribe'
                            )}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Pricing;
